import type { TmsInvoiceCreateRow, TmsInvoiceRow, TmsInvoiceSendResult } from '@numeo/types-main-web-app'
import { approveWrite as approveWriteCard, type WriteOutcome } from '../../api/writeCardApi'
import { rowKeyOf } from './invoicingRows'

/**
 * One invoice to email: the row, and the recipient, Cc, subject and message the drawer shows. An empty `to` takes the rate con's address;
 * `cc` is sent as shown (an empty list sends no Cc), so nothing is copied that the drawer did not show.
 */
export interface EmailItem {
    row: TmsInvoiceRow
    to?: string
    cc?: string[]
    subject: string
    message: string
}

/** What happened to one load's email: the invoice it went under, the recipients it reached, and the card's words when it did not go. */
export interface EmailOutcome {
    key: string
    loadNumber: string
    invoiceNumber?: string
    recipients: string[]
    sent: boolean
    message: string
}

/** The one call the batch makes: a direct write with its approval handshake. Injected so the orchestration can be tested without the wire. */
export type ApproveWrite = typeof approveWriteCard

const NOT_RETRIED = 'Not sent. The request failed, so nothing was retried. Check the invoice before sending again.'

/**
 * Emails each invoice in turn: creates its invoice first when the load has none (one load per call; a question past the per-minute budget is answered by the Send button), then
 * sends it. A call that fails is reported for its load and never retried, since a send is not idempotent.
 */
export async function sendInvoiceEmails(tabId: string, items: EmailItem[], approveWrite: ApproveWrite = approveWriteCard): Promise<EmailOutcome[]> {
    const outcomes: EmailOutcome[] = []
    for (const item of items) outcomes.push(await sendOne(tabId, item, approveWrite))
    return outcomes
}

/** The invoice number a create answer names for this load, with its id, or the card's refusal. */
const createdInvoiceOf = (outcome: WriteOutcome, loadNumber: string): { invoiceId?: string; invoiceNumber?: string; message: string } => {
    const line = outcome.structured.rows?.find((row) => row.load_number === loadNumber) as TmsInvoiceCreateRow | undefined
    return { invoiceId: line?.invoice_id, invoiceNumber: line?.invoice_number, message: line?.message ?? outcome.text }
}

async function sendOne(tabId: string, item: EmailItem, approveWrite: ApproveWrite): Promise<EmailOutcome> {
    const loadNumber = item.row.load_numbers[0] ?? ''
    const base = { key: rowKeyOf(item.row), loadNumber, recipients: [] as string[] }
    let invoiceNumber = item.row.invoice_number ?? undefined
    try {
        if (!item.row.invoice_id) {
            const created = await approveWrite(tabId, 'tms_invoices.create', { load_numbers: [loadNumber] }, { approvedInDrawer: true })
            const answer = createdInvoiceOf(created, loadNumber)
            if (!answer.invoiceNumber) return { ...base, sent: false, message: answer.message }
            invoiceNumber = answer.invoiceNumber
        }
        const sent = await approveWrite(
            tabId,
            'tms_invoices.send',
            {
                invoice_number: invoiceNumber,
                method: 'send',
                ...(item.to?.trim() ? { to: [item.to.trim()] } : {}),
                ...(item.cc ? { cc: item.cc } : {}),
                subject: item.subject,
                message: item.message,
            },
            { approvedInDrawer: true }
        )
        if (sent.status === 'question') return { ...base, invoiceNumber, sent: false, message: 'Not sent. The card asked for confirmation again.' }
        const result = sent.structured as unknown as Partial<TmsInvoiceSendResult>
        if (result.outcome === 'updated') return { ...base, invoiceNumber, recipients: result.to ?? [], sent: true, message: sent.text }
        return { ...base, invoiceNumber, sent: false, message: result.message ?? sent.text }
    } catch {
        return { ...base, invoiceNumber, sent: false, message: NOT_RETRIED }
    }
}
